<template>
  <div class="three-dots-menu">
    <v-menu
      v-bind="{ ...$props, ...$attrs }"
      open-on-click
      close-on-content-click
      transition="slide-y-transition"
      location="bottom right"
      :open-delay="0"
      :close-delay="50"
      content-class="three-dots-menu-wrapper"
    >
      <template #activator="{ props }">
        <button
          type="button"
          class="menu-container"
          aria-label="More actions"
          :disabled="disabled"
          v-bind="props"
        >
          <v-icon size="small" icon="$dots-vertical" />
        </button>
      </template>
      <ul class="menu-list">
        <li
          v-for="item in menuItems"
          :key="item.value"
          :class="['menu-list-item', item.value, { 'is-disabled': item.disabled }]"
          @click.stop="handleItemClick(item)"
        >
          <DpplTooltip
            :hide-tooltip="!item.disabled || !item.tooltip"
            tooltip-placement="left"
            teleport
          >
            <template #activator="{ onMouseenter, onMouseleave }">
              <button
                type="button"
                class="menu-list-button"
                :aria-disabled="item.disabled || undefined"
                @mouseenter="onMouseenter"
                @mouseleave="onMouseleave"
              >
                <v-icon
                  :icon="item.icon || '$edit-outline'"
                  size="20px"
                />
                <span class="item-name">
                  {{ item.text }}
                </span>
              </button>
            </template>
            <template #tooltip-content>
              <p class="text-body-2 text-textDark">
                {{ item.tooltip }}
              </p>
            </template>
          </DpplTooltip>
        </li>
      </ul>
    </v-menu>
  </div>
</template>

<script setup>
import DpplTooltip from '@/components/datapeople/DpplTooltip.vue';

const props = defineProps({
  menuItems: {
    type: Array,
    required: true,
  },
  document: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['click']);

const handleItemClick = (item) => {
  // A greyed out item shows its reason on hover and does nothing on click.
  if (item.disabled) return;

  emit('click', item.value, props.document);
};
</script>

<style lang="less" scoped>
.three-dots-menu {
  width: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.menu-container {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: @grey-400;
    border-radius: 40px;
  }

  &:hover[disabled] {
    background-color: transparent;
    cursor: default;
  }

  :deep(.v-icon) path {
    fill: @icon-default;
  }
}

.three-dots-menu-wrapper {
  box-shadow: none;
  box-sizing: border-box;

  .menu-list {
    width: 100%;
    background-color: @white;
    list-style-type: none;
    border-radius: 4px;
    border: 1px solid @stroke-default;
  }

  .menu-list-button {
    display: flex;
    width: 100%;
    gap: 8px;
    align-items: center;
    justify-content: start;
  }

  .menu-list-item {
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: start;
    gap: 8px;
    padding: 8px 12px;
    color: @text-light;

    .item-name {
      font-size: 13px;
      font-weight: 500;
      color: @text-darkest;
    }

    &:hover {
      cursor: pointer;
      background-color: @bg-canvas;
    }

    &.is-disabled {
      opacity: 0.4;

      &:hover {
        cursor: default;
        background-color: transparent;
      }
    }

    &.delete, &.delete .item-name {
      color: @basic-red;
    }
  }
}
</style>
